import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Lock, RefreshCw, SearchX, ServerCrash } from 'lucide-react';
import { Button } from '../primitives/button';
import { cn } from '../lib/cn';

/**
 * Loading, empty, error and permission states.
 *
 * Twelve states are specified (§52) and they collapse into these components. The
 * distinction that matters most: a query that *failed* is never reported as an
 * absence of data, and a filter that matched nothing is never reported as an
 * empty system.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded bg-neutral-soft',
        'after:absolute after:inset-0 after:-translate-x-full after:animate-shimmer',
        'after:bg-gradient-to-r after:from-transparent after:via-white/60 after:to-transparent',
        className,
      )}
      aria-hidden
    />
  );
}

/** Skeleton shaped like the table it replaces, so the layout does not jump. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-px" role="status" aria-label="Loading records">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 border-b border-line px-4 py-3">
          {Array.from({ length: columns }).map((_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-48' : 'w-24 flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div
      className="rounded-lg border border-line bg-surface p-4"
      role="status"
      aria-label="Loading"
    >
      <Skeleton className="h-4 w-40" />
      <div className="mt-4 space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton key={index} className={cn('h-3', index === lines - 1 ? 'w-1/3' : 'w-full')} />
        ))}
      </div>
    </div>
  );
}

export interface StateAction {
  label: string;
  onClick: () => void;
}

function BaseState({
  icon,
  title,
  message,
  action,
  secondaryAction,
  tone,
  compact,
}: {
  icon: ReactNode;
  title: string;
  message: ReactNode;
  action?: StateAction;
  secondaryAction?: StateAction;
  tone: 'neutral' | 'danger' | 'warning';
  compact?: boolean;
}) {
  const iconTone =
    tone === 'danger'
      ? 'bg-danger-soft text-danger'
      : tone === 'warning'
        ? 'bg-warning-soft text-warning'
        : 'bg-neutral-soft text-ink-muted';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        compact ? 'py-8' : 'py-16',
      )}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      <div className={cn('mb-4 flex h-11 w-11 items-center justify-center rounded-xl', iconTone)}>
        {icon}
      </div>
      <h3 className="text-md font-semibold text-ink">{title}</h3>
      <p className="mt-1 max-w-md text-base text-ink-muted">{message}</p>
      {action || secondaryAction ? (
        <div className="mt-5 flex items-center gap-2">
          {action ? (
            <Button variant="primary" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button variant="secondary" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
  secondaryAction,
  icon,
  compact,
}: {
  title: string;
  message: ReactNode;
  action?: StateAction;
  secondaryAction?: StateAction;
  icon?: ReactNode;
  compact?: boolean;
}) {
  return (
    <BaseState
      icon={icon ?? <Inbox className="h-5 w-5" />}
      title={title}
      message={message}
      action={action}
      secondaryAction={secondaryAction}
      tone="neutral"
      compact={compact}
    />
  );
}

/**
 * Nothing matched the current filters.
 *
 * Distinct from `EmptyState` because the recovery differs: the data exists, the
 * filters are hiding it, and the action is to clear them — not to create a
 * first record.
 */
export function FilteredEmptyState({
  onClear,
  entity = 'records',
  compact,
}: {
  onClear: () => void;
  entity?: string;
  compact?: boolean;
}) {
  return (
    <BaseState
      icon={<SearchX className="h-5 w-5" />}
      title="Nothing matches these filters"
      message={`There are ${entity} here, but none match what you have selected.`}
      action={{ label: 'Clear filters', onClick: onClear }}
      tone="neutral"
      compact={compact}
    />
  );
}

export function ErrorState({
  title = 'This could not be loaded',
  message = 'MicaraHR could not reach your records just now. Nothing has been changed.',
  onRetry,
  secondaryAction,
  compact,
}: {
  title?: string;
  message?: ReactNode;
  onRetry?: () => void;
  secondaryAction?: StateAction;
  compact?: boolean;
}) {
  return (
    <BaseState
      icon={<ServerCrash className="h-5 w-5" />}
      title={title}
      message={message}
      action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}
      secondaryAction={secondaryAction}
      tone="danger"
      compact={compact}
    />
  );
}

/*
 * How a permission key becomes words. The component library does not know
 * any application's permission vocabulary, so the application registers its
 * labeller once at start-up; until then a key is still turned into words
 * rather than printed as a code.
 */
let labelPermission = (key: string): string => {
  const words = key.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Register the application's permission labels. Call once, at start-up. */
export function setPermissionLabeller(labeller: (key: string) => string): void {
  labelPermission = labeller;
}

/**
 * Permission denied.
 *
 * Names the permission required. "You do not have access" tells an HR officer
 * nothing they can act on; naming the permission tells their administrator
 * exactly what to grant — by the name their role editor shows, never the
 * dotted key behind it.
 */
export function PermissionDeniedState({
  permission,
  what = 'this area',
  compact,
}: {
  permission?: string;
  what?: string;
  compact?: boolean;
}) {
  return (
    <BaseState
      icon={<Lock className="h-5 w-5" />}
      title="You do not have access to this"
      message={
        <>
          Your roles do not include the permission needed to open {what}.
          {permission ? (
            <>
              {' '}
              Ask an administrator for the{' '}
              <strong className="font-medium text-ink">“{labelPermission(permission)}”</strong>{' '}
              permission.
            </>
          ) : null}
        </>
      }
      tone="warning"
      compact={compact}
    />
  );
}

export function NotFoundState({
  noun = 'record',
  onBack,
  compact,
  title,
  message,
}: {
  noun?: string;
  onBack?: () => void;
  compact?: boolean;
  /** Override where the derived wording would be wrong or would repeat a heading. */
  title?: string;
  message?: string;
}) {
  return (
    <BaseState
      icon={<Inbox className="h-5 w-5" />}
      title={title ?? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} not found`}
      message={
        message ?? `This ${noun} is no longer available. It may have been removed or archived.`
      }
      action={onBack ? { label: 'Go back', onClick: onBack } : undefined}
      tone="neutral"
      compact={compact}
    />
  );
}

/**
 * A section that failed inside an otherwise healthy page.
 *
 * One failed dashboard panel must not take the dashboard down with it (§52), so
 * panels render this inline instead of throwing.
 */
export function SectionError({ onRetry, label }: { onRetry?: () => void; label?: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-danger-border bg-danger-soft p-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-ink">{label ?? 'This section could not load'}</p>
        <p className="mt-0.5 text-sm text-ink-secondary">The rest of this page is unaffected.</p>
      </div>
      {onRetry ? (
        <Button
          size="xs"
          variant="secondary"
          onClick={onRetry}
          leadingIcon={<RefreshCw className="h-3 w-3" />}
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/** Data older than the user expects — shown after a failed background refetch. */
export function StaleBanner({ onRefresh, since }: { onRefresh: () => void; since: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-soft px-3 py-2">
      <p className="text-sm text-ink-secondary">
        Showing data from {since}. The latest refresh did not succeed.
      </p>
      <Button size="xs" variant="secondary" onClick={onRefresh}>
        Refresh
      </Button>
    </div>
  );
}
